import * as React from "react";
import { cn } from "lib/utils";
import { Eye, EyeOff } from "lucide-react";
import { Label } from "../label";
import { FormControl, FormItem, FormMessage } from "../form";

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  isLoading?: boolean;
}

const InputGs = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, label, hidden, isLoading = false, disabled, type, ...props }, ref) => {
    const [showPassword, setShowPassword] = React.useState(false);

    if (hidden) {
      return <input type="hidden" ref={ref} {...props} />;
    }

    const isPassword = type === "password";
    const inputType = isPassword && showPassword ? "text" : type;

    return (
      <FormItem>
        <FormControl>
          <div className="grid w-full items-center gap-1.5">
            {label && (
              <Label className="font-GilroySemibold text-sm text-slate-800">
                {label}
              </Label>
            )}
            <div className="relative">
              <input
                className={cn(
                  "flex h-10 w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm ring-offset-white file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-slate-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 dark:border-slate-800 dark:bg-slate-950 dark:ring-offset-slate-950 dark:placeholder:text-slate-400 dark:focus-visible:ring-slate-300",
                  className,
                  (isLoading || isPassword) && "pr-10"
                )}
                type={inputType}
                ref={ref}
                disabled={isLoading || disabled}
                {...props}
              />

              {/* Ícone de carregando */}
              {isLoading && (
                <span className="absolute inset-y-0 right-3 flex items-center pointer-events-none">
                  <svg className="animate-spin h-4 w-4 text-slate-400" viewBox="0 0 24 24">
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                      fill="none"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                    />
                  </svg>
                </span>
              )}

              {/* Botão de mostrar/ocultar senha */}
              {isPassword && !isLoading && (
                <button
                  type="button"
                  onClick={() => setShowPassword((prev) => !prev)}
                  className="absolute inset-y-0 right-3 flex items-center text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              )}
            </div>
            <FormMessage />
          </div>
        </FormControl>
      </FormItem>
    );
  }
);

InputGs.displayName = "InputGs";
export { InputGs };
